import { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';
import { DataFreshnessBanner, PREPARING_POLL, PreparingNotice, useBackgroundRefresh } from './DataFreshness';

const platformFilters = ['All', 'IG', 'LI', 'YT', 'FB'];
// Heatmap column labels; the backend lists the connected platforms' keys in heatmapPlatforms.
const platformLabels: Record<string, string> = { ig: 'IG', li: 'LI', yt: 'YT', fb: 'FB' };
const LIVE_ANALYSIS_UNAVAILABLE = 'Live analysis is unavailable right now. Please try again.';

// Missing values (e.g. sentiment, share/save where the platform doesn't report it) show as a dash.
const show = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));

export default function PerformanceContentIntelligence() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  // When the shown analysis was made, and whether a newer one is on its way.
  const [freshness, setFreshness] = useState<{ updatedAt: string | null; refreshing: boolean }>({
    updatedAt: null,
    refreshing: false,
  });
  // Nothing stored yet: the first analysis is being prepared.
  const [preparing, setPreparing] = useState(false);

  // Puts a successful answer on the page; false when it isn't one.
  const applyAnswer = (res: any): boolean => {
    if (res?.status !== 'success') return false;
    if (res.preparing) {
      setPreparing(true);
      return true;
    }
    if (!res.data) return false;
    setPreparing(false);
    setData(res.data);
    setFreshness({ updatedAt: res.updated_at ?? null, refreshing: Boolean(res.refreshing) });
    return true;
  };

  const [platformFilter, setPlatformFilter] = useState('All');

  if (import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm relative overflow-hidden">
        {/* Background patterns */}
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-50 via-white to-blue-50/20 opacity-70 pointer-events-none" />
        <div className="absolute -top-24 -left-24 w-48 h-48 rounded-full bg-blue-50 blur-3xl opacity-50" />
        <div className="absolute -bottom-24 -right-24 w-48 h-48 rounded-full bg-teal-50 blur-3xl opacity-50" />

        {/* Icon wrapper */}
        <div className="relative mb-6 z-10">
          <div className="w-20 h-20 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center shadow-inner animate-pulse">
            <i className="ri-brain-line text-4xl text-blue-600"></i>
          </div>
          <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg border-2 border-white">
            <i className="ri-time-line text-white text-sm"></i>
          </div>
        </div>

        {/* Text */}
        <h3 className="text-2xl font-bold text-gray-900 mb-2 relative z-10">Performance & Content Intelligence</h3>
        <p className="text-sm text-gray-500 max-w-md mb-8 leading-relaxed relative z-10">
          Monitor Content Efficiency Scores, theme performance matrix, and fatigue alerts. This feature is currently under active development.
        </p>

        {/* Premium Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50/70 border border-blue-100 text-xs font-semibold text-blue-700 relative z-10">
          <i className="ri-compass-3-line"></i>
          Platform Integration Underway
        </div>
      </div>
    );
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const userId = localStorage.getItem('user_id');
        if (!userId) {
          setError('User ID not found');
          setLoading(false);
          return;
        }

        const res = await apiService.getPerformanceIntelligence(userId);
        // Sample data (is_fallback, from an older backend) is never shown as the user's.
        if (res?.is_fallback ?? res?.data?.is_fallback) {
          setError(LIVE_ANALYSIS_UNAVAILABLE);
        } else if (!applyAnswer(res)) {
          setError(res?.message || res?.data?.message || 'Failed to load performance intelligence data');
        }
      } catch (err: any) {
        setError(err.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [reloadKey]);

  // The stored analysis is shown at once; while the server refreshes it (or
  // prepares the first one), re-ask quietly and swap the new one in when
  // ready. refresh=true is the Refresh button.
  const refetchQuietly = async (refresh = false) => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return;
    const res = await apiService.getPerformanceIntelligence(userId, refresh);
    if (!(res?.is_fallback ?? res?.data?.is_fallback)) applyAnswer(res);
  };
  const freshnessState = useBackgroundRefresh(freshness.refreshing, () => refetchQuietly());
  const preparingState = useBackgroundRefresh(preparing, () => refetchQuietly(), PREPARING_POLL);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="relative w-16 h-16 mb-6">
          <div className="absolute inset-0 border-4 border-violet-100 rounded-full"></div>
          <div className="absolute inset-0 border-4 border-violet-600 rounded-full border-t-transparent animate-spin"></div>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyzing Performance Data...</h3>
        <p className="text-sm text-gray-500 max-w-md text-center">
          Fetching real engagement metrics and categorizing themes via AI.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 text-red-600 rounded-lg">
        <p>{error}</p>
        <button
          onClick={() => setReloadKey((k) => k + 1)}
          className="mt-3 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg text-xs font-semibold transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (preparing) {
    return <PreparingNotice state={preparingState} onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  if (!data) return null;

  const { cesData = [], themeBars = [], formatHeatmap = [], fatigueAlerts = [] } = data;
  const heatmapPlatforms: string[] = data.heatmapPlatforms ?? ['ig', 'li', 'yt', 'fb'];
  const heatmapGrid = { gridTemplateColumns: `repeat(${heatmapPlatforms.length + 1}, minmax(0, 1fr))` };

  // Each format's lowest score across platforms, weakest first.
  const weakFormats = formatHeatmap
    .map((f: any) => {
      const scores = heatmapPlatforms.map((p) => f[p]).filter((x: any): x is number => typeof x === 'number');
      return { format: f.format, score: scores.length ? Math.min(...scores) : null };
    })
    .filter((f: { score: number | null }) => f.score !== null)
    .sort((a: { score: number }, b: { score: number }) => a.score - b.score)
    .slice(0, 3);
  const topPost = cesData.find((c: any) => typeof c.ces === 'number');
  const sentiments = cesData.filter((c: any) => c.sentiment !== null && c.sentiment !== undefined).slice(0, 2);

  const filteredCes = cesData.filter((d: any) => {
    if (platformFilter === 'All') return true;
    const map: Record<string, string> = { IG: 'Instagram', LI: 'LinkedIn', YT: 'YouTube', FB: 'Facebook' };
    return d.platform === map[platformFilter];
  });

  const maxTheme = themeBars.length > 0 ? Math.max(...themeBars.map((t: any) => t.engagement)) : 100;

  const getHeatColor = (v: number) => {
    if (v >= 85) return 'bg-emerald-500';
    if (v >= 70) return 'bg-emerald-400';
    if (v >= 55) return 'bg-amber-400';
    if (v >= 40) return 'bg-amber-500';
    return 'bg-red-400';
  };

  return (
    <div className="space-y-6">
      <DataFreshnessBanner updatedAt={freshness.updatedAt} state={freshnessState} onRefresh={() => refetchQuietly(true)} />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
          {platformFilters.map((p) => (
            <button
              key={p}
              onClick={() => setPlatformFilter(p)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                platformFilter === p ? 'bg-violet-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Performance Intelligence Dashboard — Minimal */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Best-Performing Themes */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Best-Performing Themes (avg CES)</h4>
          <div className="space-y-3">
            {themeBars.slice(0,3).map((t: any, i: number) => (
               <div key={i} className="flex items-center justify-between">
                 <span className="text-sm text-gray-700">{t.theme}</span>
                 <span className="text-sm font-semibold text-emerald-600">{show(t.engagement)}</span>
               </div>
            ))}
            {themeBars.length === 0 && <p className="text-sm text-gray-400">—</p>}
          </div>
        </div>

        {/* Weak-Performing Formats */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Weak-Performing Formats</h4>
          <div className="space-y-3">
             {weakFormats.map((f: { format: string; score: number }, i: number) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{f.format}</span>
                    <span className="text-sm font-semibold text-red-500">{f.score}</span>
                  </div>
             ))}
             {weakFormats.length === 0 && <p className="text-sm text-gray-400">—</p>}
          </div>
        </div>

        {/* Top Post */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Top Post (CES)</h4>
          <div className="space-y-3">
            {topPost ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-gray-700 truncate">{topPost.post}</span>
                <span className="text-sm font-semibold text-emerald-600">{topPost.ces}</span>
              </div>
            ) : (
              <p className="text-sm text-gray-400">—</p>
            )}
          </div>
        </div>

        {/* Sentiment Shifts */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Sentiment Shifts</h4>
          <div className="space-y-3">
            {sentiments.map((c: any, i: number) => (
              <div key={i}>
                <p className="text-sm text-gray-700">{c.sentiment}</p>
              </div>
            ))}
            {sentiments.length === 0 && (
              <p className="text-sm text-gray-400">Not available for these posts yet.</p>
            )}
          </div>
        </div>
      </div>

      {/* CES Leaderboard */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Content Efficiency Score (CES) Leaderboard</h3>
          <span className="text-xs text-gray-500">{filteredCes.length} posts tracked</span>
        </div>
        <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Post Snippet / Theme</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Platform</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">CES</th>
                <th
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase"
                  title="Shares + saves as a % of the post's interactions, where the platform reports them"
                >
                  Share / Save Ratio
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Net Sentiment</th>
              </tr>
            </thead>
            <tbody>
              {filteredCes.map((row: any, i: number) => (
                <tr key={i} className="border-t border-gray-100 hover:bg-gray-50/50">
                  <td className="px-6 py-3.5">
                    <span className="text-gray-900 font-medium">{row.post}</span>
                    {row.theme && <span className="block text-xs text-gray-400">{row.theme}</span>}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                      row.platform.toLowerCase() === 'linkedin' ? 'bg-sky-50 text-sky-700' :
                      row.platform.toLowerCase() === 'instagram' ? 'bg-pink-50 text-pink-700' :
                      row.platform.toLowerCase() === 'youtube' ? 'bg-red-50 text-red-700' :
                      'bg-blue-50 text-blue-700'
                    }`}>
                      <i className={`${
                        row.platform.toLowerCase() === 'linkedin' ? 'ri-linkedin-box-line' :
                        row.platform.toLowerCase() === 'instagram' ? 'ri-instagram-line' :
                        row.platform.toLowerCase() === 'youtube' ? 'ri-youtube-line' :
                        'ri-facebook-box-line'
                      }`}></i>
                      {row.platform}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    {typeof row.ces === 'number' ? (
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${row.ces >= 80 ? 'bg-emerald-500' : row.ces >= 60 ? 'bg-amber-400' : 'bg-red-400'}`}
                            style={{ width: `${row.ces}%` }}
                          />
                        </div>
                        <span className={`font-semibold ${row.ces >= 80 ? 'text-emerald-600' : row.ces >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                          {row.ces}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 text-gray-700 font-medium">{show(row.shareSave)}</td>
                  <td className="px-4 py-3.5">
                    <span className={`font-semibold ${
                      row.sentiment === null || row.sentiment === undefined ? 'text-gray-400' :
                      String(row.sentiment).startsWith('+') ? 'text-emerald-600' : 'text-red-600'
                    }`}>
                      {show(row.sentiment)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Theme & Format Matrix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-1">Theme Performance Matrix</h3>
          <p className="text-xs text-gray-500 mb-5">Average CES of your posts in each theme</p>
          <div className="space-y-4">
            {themeBars.map((t: any) => (
              <div key={t.theme}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-gray-700">{t.theme}</span>
                  <span className="text-sm font-semibold text-gray-900">{show(t.engagement)}</span>
                </div>
                <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      t.engagement >= 80 ? 'bg-emerald-500' : t.engagement >= 60 ? 'bg-violet-500' : 'bg-amber-500'
                    }`}
                    style={{ width: `${(t.engagement / maxTheme) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-1">Format Performance Heatmap</h3>
          <p className="text-xs text-gray-500 mb-5">Average CES of each format on your connected platforms</p>
          <div className="space-y-1">
            <div className="grid gap-2 text-xs text-gray-500 mb-2" style={heatmapGrid}>
              <div className="font-semibold">Format</div>
              {heatmapPlatforms.map((p) => (
                <div key={p} className="text-center font-semibold">{platformLabels[p] ?? p.toUpperCase()}</div>
              ))}
            </div>
            {formatHeatmap.map((row: any) => (
              <div key={row.format} className="grid gap-2 items-center" style={heatmapGrid}>
                <div className="text-sm text-gray-700 font-medium">{row.format}</div>
                {heatmapPlatforms.map((p) => row[p]).map((v: any, idx: number) => (
                  <div key={idx} className="flex flex-col items-center gap-1">
                    {typeof v === 'number' ? (
                      <div className={`w-full h-8 rounded-md ${getHeatColor(v)} flex items-center justify-center`}>
                        <span className="text-xs font-bold text-white">{v}</span>
                      </div>
                    ) : (
                      <div className="w-full h-8 rounded-md bg-gray-100 flex items-center justify-center">
                        <span className="text-xs text-gray-400">—</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fatigue & Performance Decline Radar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
            <i className="ri-radar-line text-amber-600 text-lg"></i>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Fatigue & Performance Decline Radar</h3>
            <p className="text-xs text-gray-500">AI notes on your posts' computed scores across themes and formats</p>
          </div>
        </div>
        <div className="space-y-3">
          {fatigueAlerts.map((alert: string, i: number) => (
            <div key={i} className="flex items-start gap-3 p-4 bg-amber-50/70 border border-amber-200 rounded-lg">
              <i className="ri-error-warning-line text-amber-600 mt-0.5 flex-shrink-0"></i>
              <p className="text-sm text-amber-800 font-medium leading-relaxed">{alert}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
